import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { Language, TenantLegalDocuments } from '../../types';
import { brandT } from '../../lib/brandTerminology';
import { _t , type ShopContentLanguage } from '../../lib/i18n';
import { legalPath } from '../../lib/legalPages';
import { resolveCookieCopy } from '../../lib/legalDocuments.js';
import {
  acceptCookieNotice,
  hasAcceptedCookieNotice,
  subscribeCookieConsentChanged,
} from '../../lib/cookieConsent';
import { formCancelButtonClass, formSaveButtonClass } from '../../styles/uiStandards';
import { SafeMarkdownContent } from '../common/SafeMarkdownContent';

type CookieConsentBannerProps = {
  lang: ShopContentLanguage;
  tenantSlug: string;
  legalDocuments?: TenantLegalDocuments | null;
};

function cookieText(
  docs: TenantLegalDocuments | null | undefined,
  field: 'bannerText' | 'agree' | 'disagree' | 'leaveText',
  lang: ShopContentLanguage,
  platformKey: string,
) {
  const resolved = resolveCookieCopy(docs, field, lang);
  if (resolved.mode === 'custom') return resolved.text;
  return _t(platformKey, lang);
}

export function CookieConsentBanner({ lang, tenantSlug, legalDocuments }: CookieConsentBannerProps) {
  const [open, setOpen] = useState(() => !hasAcceptedCookieNotice());
  const [leaveWarn, setLeaveWarn] = useState(false);

  useEffect(() => subscribeCookieConsentChanged(() => {
    const accepted = hasAcceptedCookieNotice();
    setOpen(!accepted);
    if (accepted) setLeaveWarn(false);
  }), []);

  const isHe = lang === 'he';
  const agree = cookieText(legalDocuments, 'agree', lang, 'cookie.agree');
  const disagree = cookieText(legalDocuments, 'disagree', lang, 'cookie.disagree');
  const mainText = leaveWarn
    ? cookieText(legalDocuments, 'leaveText', lang, 'cookie.leaveText')
    : cookieText(legalDocuments, 'bannerText', lang, 'cookie.bannerText');

  const buttons = (
    <div className="flex shrink-0 flex-wrap gap-2">
      <button
        type="button"
        id="btn-cookie-agree"
        className={formSaveButtonClass}
        onClick={() => {
          acceptCookieNotice();
          setOpen(false);
          setLeaveWarn(false);
        }}
      >
        {agree}
      </button>
      {leaveWarn ? null : (
        <button
          type="button"
          id="btn-cookie-disagree"
          className={formCancelButtonClass}
          onClick={() => setLeaveWarn(true)}
        >
          {disagree}
        </button>
      )}
    </div>
  );

  const copy = (
    <div id="cookie-consent-text" className="min-w-0 flex-1 text-sm text-[var(--text-primary)]">
      {mainText ? (
        <SafeMarkdownContent markdown={mainText} allowLinks={false} className="text-sm" />
      ) : null}
      {!leaveWarn ? (
        <p className="mt-1">
          <Link
            to={legalPath('privacy', tenantSlug)}
            className="text-[var(--accent-primary)] underline-offset-2 hover:underline"
          >
            {brandT('legal.navPrivacy', lang)}
          </Link>
        </p>
      ) : null}
    </div>
  );

  if (!open) return null;

  return (
    <div
      id="cookie-consent-banner"
      role="dialog"
      aria-labelledby="cookie-consent-text"
      dir={isHe ? 'rtl' : 'ltr'}
      className="fixed inset-x-0 bottom-0 z-[70] border-t border-[var(--border-color)] bg-[var(--bg-primary)] px-4 py-3 shadow-[0_-8px_24px_rgba(0,0,0,0.08)]"
      style={{
        paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom, 0px))',
        paddingInlineStart: 'max(1rem, env(safe-area-inset-left, 0px))',
        paddingInlineEnd: 'max(1rem, calc(1rem + 3.5rem + env(safe-area-inset-right, 0px)))',
      }}
    >
      <div className="mx-auto flex w-full max-w-5xl flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        {copy}
        {buttons}
      </div>
    </div>
  );
}
